import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import styles from './FloatingWindows.module.css';
import {
  X,
  Minus,
  Search,
  ArrowUpDown,
  GripVertical,
  LockKeyhole,
  Check,
  Sun,
  Moon,
} from 'lucide-react';
import { zamienNaGodziny } from 'actions/zamienNaGodziny';
import { formatujDatePoPolsku } from 'actions/formatujDatePoPolsku';
import { AppContext } from 'context/AppContext';
import { getNameOfElementTyp } from 'actions/getNameOfElementTyp';

const STORAGE_KEY = 'gantt_floating_windows_state';
const THEME_STORAGE_KEY = 'gantt_floating_windows_theme';

// Podkomponent zawartości okna z obsługą przestawiania prac (wrzucanie klocka w klocek)
const WindowTaskContent = React.memo(
  ({
    procesorId,
    rawStages,
    statusFilter,
    searchQuery,
    sortAsc = true,
    isLightMode = false,
    selectedGlobalId,
    onSelectAndCenterStage,
    onSelectStage,
    canDragOnGant,
    moveable = true,
    isAnyResizing = false,
    onDragStart,
    onDragOverStage,
    onDropOnStage,
    onDragEnd,
    onMarkAsRead,
  }) => {
    const selectedRowRef = useRef(null);
    const contextApp = useContext(AppContext);
    const [dragOverStageId, setDragOverStageId] = useState(null);
    const [draggingStageId, setDraggingStageId] = useState(null);

    // Czyszczenie podświetlenia po zakończeniu jakiegokolwiek przeciągania (nawet z zewnątrz okna)
    useEffect(() => {
      const clearDragStates = () => {
        setDragOverStageId(null);
        setDraggingStageId(null);
      };
      window.addEventListener('dragend', clearDragStates);
      window.addEventListener('drop', clearDragStates);
      return () => {
        window.removeEventListener('dragend', clearDragStates);
        window.removeEventListener('drop', clearDragStates);
      };
    }, []);

    // Te same reguły co w GanttStageBar / GanttNewStages + blokada kłódki
    const checkIsStageDraggable = useCallback(
      (stage) => {
        if (!stage) return false;
        if (!moveable || isAnyResizing) return false;
        if (stage.zablokowany == 1) return false;
        if (
          typeof canDragOnGant === 'function' &&
          !canDragOnGant(stage.proces_nazwa_id)
        ) {
          return false;
        }
        return true;
      },
      [moveable, isAnyResizing, canDragOnGant]
    );

    // Start przeciągania z listy podpięty pod usePreciseDragDropGant
    const handleRowDragStart = (stage, e, isDraggable) => {
      if (!isDraggable) {
        e.preventDefault();
        return;
      }

      setDraggingStageId(stage.global_id || stage.id);

      // Zerujemy grabOffset dla usePreciseDragDropGant (useGrabOffset: true),
      // aby linia celownika na wykresie była idealnie pod kursorem myszy
      const currentTargetProxy = new Proxy(e.currentTarget, {
        get(ctTarget, ctProp) {
          if (ctProp === 'getBoundingClientRect') {
            return () => ({
              left: e.clientX,
              top: e.clientY,
              right: e.clientX,
              bottom: e.clientY,
              width: 0,
              height: 0,
            });
          }
          const val = ctTarget[ctProp];
          return typeof val === 'function' ? val.bind(ctTarget) : val;
        },
      });

      const eventForHook = new Proxy(e, {
        get(target, prop) {
          if (prop === 'currentTarget') return currentTargetProxy;
          const val = target[prop];
          return typeof val === 'function' ? val.bind(target) : val;
        },
      });

      if (typeof onDragStart === 'function') {
        onDragStart(stage, eventForHook);
      }
    };

    // Przeciąganie nad innym wierszem w okienku (wspiera wrzucanie klocka w klocek)
    const handleRowDragOver = (targetStage, e) => {
      e.preventDefault();
      e.stopPropagation();

      const targetId = targetStage.global_id || targetStage.id;
      if (dragOverStageId !== targetId) {
        setDragOverStageId(targetId);
      }

      if (typeof onDragOverStage === 'function') {
        onDragOverStage(targetStage, procesorId, e);
      }
    };

    const handleRowDragLeave = (targetStage, e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      const targetId = targetStage.global_id || targetStage.id;
      if (dragOverStageId === targetId) {
        setDragOverStageId(null);
      }
    };

    // Upuszczenie pracy na inną pracę w okienku -> wywołuje tę samą funkcję co na wykresie
    const handleRowDrop = (targetStage, e) => {
      e.preventDefault();
      e.stopPropagation();

      setDragOverStageId(null);
      setDraggingStageId(null);

      if (typeof onDropOnStage === 'function') {
        onDropOnStage(targetStage, procesorId, e);
      }
    };

    const handleRowDragEnd = (e) => {
      setDragOverStageId(null);
      setDraggingStageId(null);
      if (typeof onDragEnd === 'function') {
        onDragEnd(e);
      }
    };

    const sortedAndFilteredStages = useMemo(() => {
      if (!rawStages) return [];

      // 1. Sortowanie chronologiczne wg daty rozpoczęcia
      let result = [...rawStages].sort((a, b) => {
        const timeA = new Date(a.start).getTime();
        const timeB = new Date(b.start).getTime();
        return sortAsc ? timeA - timeB : timeB - timeA;
      });

      // 2. Filtrowanie po statusie
      if (statusFilter && statusFilter !== 'all') {
        result = result.filter(
          (stage) => String(stage.status) === String(statusFilter)
        );
      }

      // 3. Wyszukiwanie po numerze (nr), roku (rok), kliencie lub tytule
      if (searchQuery && searchQuery.trim() !== '') {
        const q = searchQuery.trim().toLowerCase();
        result = result.filter((stage) => {
          const nrStr = String(stage.nr || '').toLowerCase();
          const rokStr = String(stage.rok || '').toLowerCase();
          const rokShort = rokStr.substring(2, 4);
          const fullFormatSlash = `${nrStr}/${rokStr}`;
          const fullFormatShort = `${nrStr}/${rokShort}`;
          const fullFormatUnderscore = `${nrStr}_${rokShort}`;
          const klientStr = String(stage.firma_nazwa || '').toLowerCase();
          const tytulStr = String(stage.tytul || '').toLowerCase();

          return (
            nrStr.includes(q) ||
            rokStr.includes(q) ||
            fullFormatSlash.includes(q) ||
            fullFormatShort.includes(q) ||
            fullFormatUnderscore.includes(q) ||
            klientStr.includes(q) ||
            tytulStr.includes(q)
          );
        });
      }

      return result;
    }, [rawStages, statusFilter, searchQuery, sortAsc]);

    // Przewinięcie listy do zaznaczonego klocka
    useEffect(() => {
      if (selectedGlobalId && selectedRowRef.current) {
        selectedRowRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [selectedGlobalId]);

    const formatDateShort = (dateStr) => {
      if (!dateStr) return '--:--';
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('pl-PL', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };


        const getGradient = (stage) => {
      if (stage.status == 4 && stage.etap == 16) return '#e116d7'; // zakończony spec.
      if (stage.status == 4) return isLightMode ?  'linear-gradient(to bottom, #768d87 25%, #6c7c7c 100%)':'linear-gradient(to bottom, #6b72802f 5%, #6b72802f 100%)'; // zakończony (szary)

      if (
        stage.procesor_id == 1 ||
        stage.procesor_id == 2 ||
        stage.procesor_id == 3
      ) {
        if (
          stage.pliki_etap == 1 ||
          stage.pliki_etap == 2 ||
          stage.pliki_etap == 3
        )
          return isLightMode ? 'rgba(100, 116, 139, 0.14)' : '#ffffff49'; // brak plików / przygotowanie
        if (stage.pliki_etap == 4 || stage.pliki_etap == 5)
          return isLightMode ? 'linear-gradient(to bottom, #d7e6f5 5%, #abc4db 100%)':'linear-gradient(to bottom, #0284c747 5%, #0284c747 100%)'; // pliki / akcept (niebieski)

        
        if (
          stage.pliki_etap == 6 ||
          stage.pliki_etap == 7 ||
          stage.pliki_etap == 8
        )
          return isLightMode ? 'linear-gradient(to bottom, #b8e356 25%, #a5cc52 100%)': 'linear-gradient(to bottom, #a9ce545e 25%, #a9ce545e 100%)'; // rip / zaświecone (zielony)
      }

      

      if (stage.typ_grupy == 1) return '#6b72802f';
      if (stage.status == 1)
        return isLightMode ? 'rgba(100, 116, 139, 0.16)' : '#ffffff56'; // niedostępne
      if (stage.status == 2) return '#a9ce545e'; // oczekujące (zielony)
      if (stage.status == 3) return '#facc1573'; // w trakcie (żółty)

      return isLightMode ? '#f8fafc' : '#374151';
    };

    // Kolor tła wiersza analogicznie do statusów/etapów plików
    const getBackGroudColor = (stage) => {
      if (stage.status == 4 && stage.etap == 16) return '#e116d7'; // zakończony spec.
      if (stage.status == 4) return isLightMode ?  '#6b7280ea':'#6b72802f'; // zakończony (szary)

      if (
        stage.procesor_id == 1 ||
        stage.procesor_id == 2 ||
        stage.procesor_id == 3
      ) {
        if (
          stage.pliki_etap == 1 ||
          stage.pliki_etap == 2 ||
          stage.pliki_etap == 3
        )
          return isLightMode ? 'rgba(100, 116, 139, 0.14)' : '#ffffff49'; // brak plików / przygotowanie
        if (stage.pliki_etap == 4 || stage.pliki_etap == 5)
          return isLightMode ? 'rgba(39, 156, 214, 0.19)':'rgba(2, 132, 199, 0.28)'; // pliki / akcept (niebieski)
        if (
          stage.pliki_etap == 6 ||
          stage.pliki_etap == 7 ||
          stage.pliki_etap == 8
        )
          return isLightMode ? '#a9ce54de': '#a9ce545e'; // rip / zaświecone (zielony)
      }

      if (stage.typ_grupy == 1) return '#6b72802f';
      if (stage.status == 1)
        return isLightMode ? 'rgba(100, 116, 139, 0.16)' : '#ffffff56'; // niedostępne
      if (stage.status == 2) return '#a9ce545e'; // oczekujące (zielony)
      if (stage.status == 3) return '#facc1573'; // w trakcie (żółty)

      return isLightMode ? '#f8fafc' : '#374151';
    };

    // Zachowany w 100% oryginalny accentColor
    const getAccentColor = (stage) => {
      if (stage.status == 4 && stage.etap == 16) return '#e116d7'; // zakończony spec.
      if (stage.status == 4) return isLightMode ? '#4a4c52':'#6b7280'; // zakończony (szary)

      if (stage.procesor_id == 1) {
        if (
          stage.pliki_etap == 1 ||
          stage.pliki_etap == 2 ||
          stage.pliki_etap == 3
        )
          return '#9ca3af'; // brak plików / przygotowanie
        if (stage.pliki_etap == 4 || stage.pliki_etap == 5)
          return isLightMode ? '#278fbb':'#38bdf8'; // pliki / akcept (niebieski)
        if (
          stage.pliki_etap == 6 ||
          stage.pliki_etap == 7 ||
          stage.pliki_etap == 8
        )
          return isLightMode ? '#6b8d1c' :'#a9ce54'; // rip / zaświecone (zielony)
      }

      if (stage.typ_grupy == 1) return '#9ca3af';
      if (stage.status == 1) return '#9ca3af'; // niedostępne
      if (stage.status == 2) return isLightMode ? '#6b8d1c':'#a9ce54'; // oczekujące (zielony)
      if (stage.status == 3) return isLightMode ? '#bb9c1f':'#facc15'; // w trakcie (żółty)

      return '#38bdf8';
    };

    return (
      <div className={styles.taskList}>
        {sortedAndFilteredStages.length === 0 ? (
          <div className={styles.emptyState}>
            Brak zadań spełniających kryteria
          </div>
        ) : (
          sortedAndFilteredStages.map((s, idx) => {
            const stageId = s.global_id || s.id;
            const isSelected =
              selectedGlobalId != null && s.global_id === selectedGlobalId;
            const isDragOver =
              dragOverStageId != null &&
              dragOverStageId === stageId &&
              draggingStageId !== stageId;
            const isBeingDragged =
              draggingStageId != null && draggingStageId === stageId;

            const isDraggable = checkIsStageDraggable(s);
            const accentColor = getAccentColor(s);
            const backgroundColor = getBackGroudColor(s);
            const gradient = getGradient(s);
            const rokShort = String(s.rok || '').substring(2, 4);
            const orderNumStr = s.nr ? `${s.nr}_${rokShort}` : '';
            const elementSkrot =
              getNameOfElementTyp(s.typ_elementu, contextApp?.typ_elementu) ||
              '';

            const arkuszeInfo =
              s.arkusze_numery &&
              s.arkusze_numery !== '' &&
              s.proces_nazwa_id != 1
                ? ` (ark: ${s.arkusze_numery})`
                : '';

            const wymiarPapieru =
              s.arkusz_szerokosc && s.arkusz_wysokosc
                ? `${s.arkusz_szerokosc}x${s.arkusz_wysokosc}`
                : '';

            const papierStr = [
              wymiarPapieru,
              s.nazwa_papieru,
              s.gramatura,
              s.wykonczenie,
            ]
              .filter(Boolean)
              .join(' ');

            const spedycjaStr = formatujDatePoPolsku(s.data_spedycji);

            const rowTooltip = isDraggable
              ? 'Przeciągnij na inną pracę lub wykres | Kliknij dwukrotnie, aby wycentrować'
              : s.zablokowany == 1
              ? 'Etap zablokowany (kłódka)'
              : !moveable
              ? 'Przenoszenie klocków jest wyłączone na górnym pasku'
              : 'Brak uprawnień do przesuwania tego procesu';

            return (
              <div
                key={`${stageId || 'stage'}-${s.nr || 'nr'}-${idx}`}
                ref={isSelected ? selectedRowRef : null}
                draggable={isDraggable}
                onDragStart={(e) => handleRowDragStart(s, e, isDraggable)}
                onDragOver={(e) => handleRowDragOver(s, e)}
                onDragLeave={(e) => handleRowDragLeave(s, e)}
                onDrop={(e) => handleRowDrop(s, e)}
                onDragEnd={handleRowDragEnd}
                className={`${styles.taskRow} ${
                  isSelected ? styles.selectedTaskRow : ''
                } ${isDragOver ? styles.dragOverTaskRow : ''} ${
                  isBeingDragged ? styles.draggingSourceRow : ''
                }`}
                onClick={() => onSelectStage && onSelectStage(s)}
                onDoubleClick={() => {
                  onSelectStage && onSelectStage(s);
                  onSelectAndCenterStage && onSelectAndCenterStage(s);
                }}
                title={rowTooltip}
                style={{
                  borderLeft: `4px solid ${accentColor}`,
                  cursor: isDraggable ? 'grab' : 'not-allowed',
                  opacity: isBeingDragged ? 0.45 : isDraggable ? 1 : 0.75,
                  background: gradient,

                }}
              >
                {/* Ikona uchwytu lub pomarańczowej kłódki */}
                <div
                  className={styles.dragHandleIcon}
                  style={{
                    color:
                      s.zablokowany == 1
                        ? 'orange'
                        : isDraggable
                        ? '#9ca3af'
                        : '#6b7280',
                  }}
                >
                  {s.zablokowany == 1 ? (
                    <LockKeyhole size={14} />
                  ) : (
                    <GripVertical size={14} />
                  )}
                </div>

                <div className={styles.taskRowInner}>
                  {/* LINIA 1: Nr_Rok + Klient + Tytuł (po lewej) oraz Plakietki (po prawej) */}
                  <div className={styles.rowHeader}>
                    <span>
                      <strong
                        className={styles.orderAndClient}
                        style={{ color: accentColor }}
                        title={`${orderNumStr} ${
                          s.firma_nazwa || s.uwagi || ''
                        }`}
                      >
                        {orderNumStr} {s.firma_nazwa}{' '}
                      </strong>
                      <span
                        style={{
                          color: isLightMode ? '#1e293b' : '#dfdbdb',
                          fontWeight: 'bold',
                        }}
                      >
                        {s.tytul || s.uwagi || ''}
                      </span>
                      {arkuszeInfo && (
                        <span className={styles.sheetsHighlight}>
                          {arkuszeInfo}
                        </span>
                      )}
                    </span>

                    <div className={styles.badgesGroup}>
                      {s.nowa == 1 &&
                        (onMarkAsRead ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkAsRead(e, s);
                            }}
                            className={styles.newStageBtn}
                            title="Oznacz jako przeczytane (zdejmij kropkę Nowa)"
                          >
                            <Check size={10} />
                            NOWA
                          </button>
                        ) : (
                          <span className={styles.newStageBadge}>NOWA</span>
                        ))}

                      {elementSkrot && (
                        <span
                          className={styles.elementBadge}
                          style={{ color: accentColor }}
                          title="Typ elementu"
                        >
                          {elementSkrot}
                        </span>
                      )}

                      {spedycjaStr && (
                        <span
                          style={{ color: accentColor }}
                          className={styles.shippingBadge}
                          title={`Data spedycji (Na): ${spedycjaStr}`}
                        >
                          Na: {spedycjaStr}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* LINIA 2: Harmonogram i technologia */}
                  <div className={styles.rowMeta} title={papierStr}>
                    <span>
                      Start:{' '}
                      <strong className={styles.metaLight}>
                        {formatDateShort(s.start)}
                      </strong>
                    </span>
                    <span className={styles.sep} style={{ color: accentColor }}>|</span>
                    <span>
                      Czas:{' '}
                      <strong className={styles.metaGreen}>
                        {zamienNaGodziny(s.czas)}
                      </strong>
                    </span>
                    <span className={styles.sep} style={{ color: accentColor }}>|</span>
                    <span>
                      Koniec:{' '}
                      <strong className={styles.metaLight}>
                        {formatDateShort(s.end)}
                      </strong>
                    </span>

                    <span>
                      Nakł:{' '}
                      <strong className={styles.metaLight}>
                        {s.naklad || 0}
                      </strong>
                    </span>
                    <span className={styles.sep} style={{ color: accentColor }}>|</span>
                    <span>
                      Przel:{' '}
                      <strong className={styles.metaLight}>
                        {s.przeloty || 0}
                      </strong>
                    </span>
                    {s.ilosc_narzadow ? (
                      <>
                        <span className={styles.sep} style={{ color: accentColor }}>|</span>
                        <span>
                          Ark:{' '}
                          <strong className={styles.metaLight}>
                            {s.ilosc_narzadow}
                          </strong>
                        </span>
                      </>
                    ) : null}
                    {papierStr && (
                      <>
                        <span className={styles.sep} style={{ color: accentColor }}>|</span>
                        <span className={styles.paperText}>{papierStr}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  }
);

export const FloatingWindows = forwardRef(
  (
    {
      procesory,
      groupedStages,
      selectedGrup,
      onSelectStage,
      onSelectAndCenterStage,
      onWindowsChange,
      canDragOnGant,
      moveable = true,
      isAnyResizing = false,
      onDragStart,
      onDragOverStage,
      onDropOnStage,
      onDragEnd,
      onMarkAsRead,
    },
    ref
  ) => {
    const [windows, setWindows] = useState(() => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : [];
      } catch (e) {
        console.error('Błąd odczytu localStorage:', e);
        return [];
      }
    });

    // Wspólne ustawienie motywu dla wszystkich okienek zapisane w przeglądarce
    const [isLightMode, setIsLightMode] = useState(() => {
      try {
        return localStorage.getItem(THEME_STORAGE_KEY) === 'light';
      } catch (e) {
        return false;
      }
    });

    const toggleTheme = useCallback(() => {
      setIsLightMode((prev) => {
        const next = !prev;
        try {
          localStorage.setItem(THEME_STORAGE_KEY, next ? 'light' : 'dark');
        } catch (e) {
          console.error('Błąd zapisu motywu do localStorage:', e);
        }
        return next;
      });
    }, []);

    // Helper wyciągający klucz grupy procesora
    const getProcGroup = useCallback(
      (proc) => {
        if (!proc) return null;
        const directGroup =
          proc.grupa ??
          proc.grupa_id ??
          proc.proces_nazwa_id ??
          proc.typ_grupy ??
          proc.kategoria ??
          proc.typ;

        if (directGroup != null) return directGroup;

        // Fallback: pobranie proces_nazwa_id z pierwszego etapu tego procesora
        const stagesForProc = groupedStages?.[proc.id];
        if (stagesForProc && stagesForProc.length > 0) {
          return stagesForProc[0].proces_nazwa_id ?? null;
        }
        return null;
      },
      [groupedStages]
    );

    useEffect(() => {
      if (!selectedGrup || !selectedGrup.procesor_id) return;

      setWindows((prev) => {
        const targetProcId = Number(selectedGrup.procesor_id);
        const win = prev.find((w) => Number(w.procesor_id) === targetProcId);

        if (!win || !win.isOpen) return prev;

        const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);

        if (win.isMinimized === false && win.zIndex === maxZ) return prev;

        const updated = prev.map((w) =>
          Number(w.procesor_id) === targetProcId
            ? { ...w, isMinimized: false, zIndex: maxZ + 1 }
            : w
        );

        saveToLocalStorage(updated);
        return updated;
      });
    }, [selectedGrup]);

    const dragRef = useRef(null);
    const animFrameRef = useRef(null);

    const openWindowsSignature = useMemo(() => {
      return windows
        .filter((w) => w.isOpen)
        .map((w) => `${w.id}-${w.procesor_id}-${w.isMinimized}`)
        .join('|');
    }, [windows]);

    useEffect(() => {
      if (onWindowsChange) {
        onWindowsChange(windows.filter((w) => w.isOpen));
      }
    }, [openWindowsSignature, onWindowsChange]);

    const saveToLocalStorage = (newWindows) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newWindows));
      } catch (e) {
        console.error('Błąd zapisu do localStorage:', e);
      }
    };

    const bringToFront = (id) => {
      setWindows((prev) => {
        const target = prev.find((w) => w.id === id);
        if (!target) return prev;

        const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
        const topCount = prev.filter((w) => (w.zIndex || 1) === maxZ).length;

        if (
          target.isOpen &&
          !target.isMinimized &&
          target.zIndex === maxZ &&
          topCount === 1
        ) {
          return prev;
        }

        const updated = prev.map((w) =>
          w.id === id
            ? { ...w, isOpen: true, isMinimized: false, zIndex: maxZ + 1 }
            : w
        );
        saveToLocalStorage(updated);
        return updated;
      });
    };

    // Przełączenie procesora wewnątrz tego samego okna (ze stopki)
    const changeWindowProcessor = (windowId, newProcesorId) => {
      const procIdNum = Number(newProcesorId);
      setWindows((prev) => {
        const proc = procesory?.find((p) => Number(p.id) === procIdNum);
        const updated = prev.map((w) =>
          w.id === windowId
            ? {
                ...w,
                procesor_id: procIdNum,
                title: proc?.nazwa || `Procesor ${procIdNum}`,
              }
            : w
        );
        saveToLocalStorage(updated);
        return updated;
      });
    };

    const closeWindow = (id) => {
      setWindows((prev) => {
        const updated = prev.map((w) =>
          w.id === id ? { ...w, isOpen: false } : w
        );
        saveToLocalStorage(updated);
        return updated;
      });
    };

    const closeAll = () => {
      setWindows((prev) => {
        const updated = prev.map((w) => ({ ...w, isOpen: false }));
        saveToLocalStorage(updated);
        return updated;
      });
    };

    const addWindow = (procesorId) => {
      const procIdNum = Number(procesorId);
      setWindows((prev) => {
        const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
        const existing = prev.find((w) => Number(w.procesor_id) === procIdNum);

        let updated;
        if (existing) {
          updated = prev.map((w) =>
            w.id === existing.id
              ? { ...w, isOpen: true, isMinimized: false, zIndex: maxZ + 1 }
              : w
          );
        } else {
          const proc = procesory?.find((p) => Number(p.id) === procIdNum);
          const newWin = {
            id: `win-proc-${procIdNum}-${Date.now()}`,
            title: proc?.nazwa || `Procesor ${procIdNum}`,
            procesor_id: procIdNum,
            x: 80 + ((prev.length * 25) % 200),
            y: 80 + ((prev.length * 25) % 150),
            width: 380,
            height: 360,
            isOpen: true,
            isMinimized: false,
            zIndex: maxZ + 1,
            statusFilter: 'all',
            searchQuery: '',
            sortAsc: true,
          };
          updated = [...prev, newWin];
        }
        saveToLocalStorage(updated);
        return updated;
      });
    };

    const centerWindow = (id) => {
      setWindows((prev) => {
        const maxZ = Math.max(...prev.map((w) => w.zIndex || 1), 10);
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const winWidth = 460;
        const winHeight = 420;

        const updated = prev.map((w) => {
          if (w.id === id) {
            const newX = Math.max(
              20,
              Math.round((viewportWidth - winWidth) / 2)
            );
            const newY = Math.max(
              20,
              Math.round((viewportHeight - winHeight) / 2)
            );

            return {
              ...w,
              width: winWidth,
              height: winHeight,
              x: newX,
              y: newY,
              isOpen: true,
              isMinimized: false,
              zIndex: maxZ + 1,
            };
          }
          return w;
        });

        saveToLocalStorage(updated);
        return updated;
      });
    };

    useImperativeHandle(ref, () => ({
      addWindow,
      closeWindow,
      bringToFront,
      closeAll,
      centerWindow,
    }));

    const handleMouseMove = (e) => {
      if (!dragRef.current) return;

      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }

      const {
        type,
        id,
        startX,
        startY,
        initialX,
        initialY,
        initialWidth,
        initialHeight,
      } = dragRef.current;
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      animFrameRef.current = requestAnimationFrame(() => {
        setWindows((prev) =>
          prev.map((w) => {
            if (w.id !== id) return w;

            if (type === 'move') {
              return {
                ...w,
                x: Math.max(0, initialX + deltaX),
                y: Math.max(0, initialY + deltaY),
              };
            }

            if (type === 'resize') {
              return {
                ...w,
                width: Math.max(300, initialWidth + deltaX),
                height: Math.max(160, initialHeight + deltaY),
              };
            }

            return w;
          })
        );
      });
    };

    const handleMouseUp = () => {
      if (dragRef.current) {
        dragRef.current = null;
        setWindows((latest) => {
          saveToLocalStorage(latest);
          return latest;
        });
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDownHeader = (e, id) => {
      if (
        e.target.closest('button') ||
        e.target.closest('select') ||
        e.target.closest('input')
      )
        return;
      bringToFront(id);

      const win = windows.find((w) => w.id === id);
      if (!win) return;

      dragRef.current = {
        type: 'move',
        id,
        startX: e.clientX,
        startY: e.clientY,
        initialX: win.x,
        initialY: win.y,
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDownResize = (e, id) => {
      e.stopPropagation();
      bringToFront(id);

      const win = windows.find((w) => w.id === id);
      if (!win) return;

      dragRef.current = {
        type: 'resize',
        id,
        startX: e.clientX,
        startY: e.clientY,
        initialWidth: win.width,
        initialHeight: win.height,
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    };

    const toggleSort = (id) => {
      setWindows((prev) => {
        const updated = prev.map((w) =>
          w.id === id
            ? { ...w, sortAsc: w.sortAsc === false ? true : false }
            : w
        );
        saveToLocalStorage(updated);
        return updated;
      });
    };

    const handleSearchChange = (id, searchValue) => {
      setWindows((prev) => {
        const updated = prev.map((w) =>
          w.id === id ? { ...w, searchQuery: searchValue } : w
        );
        saveToLocalStorage(updated);
        return updated;
      });
    };

    return (
      <div className={styles.windowsOverlay}>
        {windows
          .filter((w) => w.isOpen)
          .map((win) => {
            const currentProc = procesory?.find(
              (p) => Number(p.id) === Number(win.procesor_id)
            );
            const procName = currentProc?.nazwa || win.title;
            const rawStages = groupedStages?.[win.procesor_id] || [];
            const isSortAsc = win.sortAsc !== false;

            // Procesory z tej samej grupy do wyświetlenia w stopce
            const currentGroupKey = getProcGroup(currentProc);
            const sameGroupProcessors =
              currentGroupKey != null && Array.isArray(procesory)
                ? procesory.filter((p) => getProcGroup(p) === currentGroupKey)
                : [];

            return (
              <div
                key={win.id}
                className={`${styles.window} ${
                  isLightMode ? styles.lightWindow : ''
                }`}
                style={{
                  left: `${win.x}px`,
                  top: `${win.y}px`,
                  width: `${win.width}px`,
                  height: win.isMinimized ? 'auto' : `${win.height}px`,
                  zIndex: win.zIndex || 10,
                }}
                onMouseDown={() => bringToFront(win.id)}
              >
                <div
                  className={styles.windowHeader}
                  onMouseDown={(e) => handleMouseDownHeader(e, win.id)}
                >
                  <div className={styles.windowTitle} title={procName}>
                    <span className={styles.titleText}>{procName}</span>
                  </div>

                  <div className={styles.headerSearch}>
                    <Search size={11} className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Szukaj..."
                      value={win.searchQuery || ''}
                      onChange={(e) =>
                        handleSearchChange(win.id, e.target.value)
                      }
                      className={styles.headerSearchInput}
                    />
                    {win.searchQuery && (
                      <button
                        onClick={() => handleSearchChange(win.id, '')}
                        className={styles.clearSearchBtn}
                        title="Wyczyść wyszukiwanie"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>

                  <div className={styles.windowControls}>
                    <button
                      onClick={toggleTheme}
                      className={styles.themeBtn}
                      title={
                        isLightMode
                          ? 'Przełącz na motyw ciemny (dla wszystkich okien)'
                          : 'Przełącz na motyw jasny (dla wszystkich okien)'
                      }
                    >
                      {isLightMode ? <Moon size={11} /> : <Sun size={11} />}
                    </button>
                    <button
                      onClick={() => toggleSort(win.id)}
                      className={styles.sortBtn}
                      title={
                        isSortAsc
                          ? 'Sortowanie od najstarszych (kliknij, aby odwrócić)'
                          : 'Sortowanie od najnowszych (kliknij, aby odwrócić)'
                      }
                    >
                      <ArrowUpDown size={11} />
                    </button>
                    <button
                      onClick={() => closeWindow(win.id)}
                      title="Zamknij"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>

                {!win.isMinimized && (
                  <div className={styles.windowContent}>
                    <WindowTaskContent
                      procesorId={win.procesor_id}
                      rawStages={rawStages}
                      statusFilter={win.statusFilter}
                      searchQuery={win.searchQuery}
                      sortAsc={isSortAsc}
                      isLightMode={isLightMode}
                      selectedGlobalId={selectedGrup?.global_id}
                      onSelectStage={onSelectStage}
                      onSelectAndCenterStage={onSelectAndCenterStage}
                      canDragOnGant={canDragOnGant}
                      moveable={moveable}
                      isAnyResizing={isAnyResizing}
                      onDragStart={onDragStart}
                      onDragOverStage={onDragOverStage}
                      onDropOnStage={onDropOnStage}
                      onDragEnd={onDragEnd}
                      onMarkAsRead={onMarkAsRead}
                    />
                  </div>
                )}

                {/* Stopka z procesorami z tej samej grupy */}
                {!win.isMinimized && sameGroupProcessors.length > 1 && (
                  <div className={styles.windowFooter}>
                    {sameGroupProcessors.map((proc) => {
                      const isActive =
                        Number(proc.id) === Number(win.procesor_id);
                      const procStages = groupedStages?.[proc.id] || [];
                      const hasNewStage = procStages.some((s) => s.nowa == 1);

                      return (
                        <button
                          key={proc.id}
                          type="button"
                          onClick={() =>
                            changeWindowProcessor(win.id, proc.id)
                          }
                          className={`${styles.footerProcBtn} ${
                            isActive ? styles.footerProcBtnActive : ''
                          }`}
                          title={`Przełącz na: ${proc.nazwa} (${procStages.length} zadań)`}
                        >
                          <span className={styles.footerProcName}>
                            {proc.skrot || proc.nazwa}
                          </span>
                          <span className={styles.footerProcCount}>
                            {procStages.length}
                          </span>
                          {hasNewStage && (
                            <span
                              className={styles.footerNewDot}
                              title="Zawiera nowe zadania"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {!win.isMinimized && (
                  <div
                    className={styles.resizeHandle}
                    onMouseDown={(e) => handleMouseDownResize(e, win.id)}
                  />
                )}
              </div>
            );
          })}
      </div>
    );
  }
);

FloatingWindows.displayName = 'FloatingWindows';